import { useMemo } from 'react';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import styles from '../MemberReports.module.css';

const memberName   = (o) => o.member?.name || o.userId?.name || '—';
const categoryName = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';
const daysPending  = (o) => Math.floor((Date.now() - new Date(o.offeringDate)) / 86_400_000);

export default function OfferingAgingReport({ offerings, filterCategoryId, filterMemberId, categories }) {
  const selectedCat = filterCategoryId
    ? categories.find((c) => String(c.id) === filterCategoryId)?.name
    : null;

  const visible = useMemo(() => offerings
    .filter((o) => ['pending', 'partial'].includes(o.status) && Number(o.pendingAmount) > 0)
    .filter((o) => !filterMemberId || String(o.userId?.id ?? o.userId) === filterMemberId)
    .filter((o) => !selectedCat || categoryName(o) === selectedCat)
    .sort((a, b) => daysPending(b) - daysPending(a)),
  [offerings, filterMemberId, selectedCat]);

  const totalOffered  = visible.reduce((s, o) => s + (Number(o.offeredAmount)  || 0), 0);
  const totalPaid     = visible.reduce((s, o) => s + (Number(o.paidAmount)     || 0), 0);
  const totalPending  = visible.reduce((s, o) => s + (Number(o.pendingAmount)  || 0), 0);

  return (
    <div className="card">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Member</th>
              <th>Category</th>
              <th>Offering Date</th>
              <th className={styles.amtRight}>Offered (₹)</th>
              <th className={styles.amtRight}>Paid (₹)</th>
              <th className={styles.amtRight}>Pending (₹)</th>
              <th style={{ textAlign: 'center' }}>Days Pending</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={8} className="empty-state">No pending offerings found.</td></tr>
            )}
            {visible.map((o, i) => (
              <tr key={o.id ?? i}>
                <td>{i + 1}</td>
                <td>{memberName(o)}</td>
                <td>{categoryName(o)}</td>
                <td>{formatDate(o.offeringDate)}</td>
                <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                <td className={`${styles.amtRight} ${styles.pending}`}>
                  <strong>{fmtCurrency(o.pendingAmount)}</strong>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className={`badge ${daysPending(o) > 90 ? 'badge-rejected' : daysPending(o) > 30 ? 'badge-pending' : 'badge-draft'}`}>
                    {daysPending(o)}d
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          {visible.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4}>
                  <strong>Total ({visible.length} offering{visible.length !== 1 ? 's' : ''})</strong>
                </td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalOffered)}</strong></td>
                <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totalPending)}</strong></td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
